// frontend/src/marketing/pages/BlogIndexPage.tsx
//
// Blog index page — lists every published post fetched from
// /api/public/site/blog. Falls back to a friendly empty state when no posts
// have been published.

import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, FileText, Calendar } from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { MarketingPageHead } from '../MarketingPageHead';
import { listBlogPosts, type BlogPost } from '../api';

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

export function BlogIndexPage() {
  const [posts, setPosts] = React.useState<BlogPost[] | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    listBlogPosts({ limit: 50 })
      .then((p) => { if (!cancelled) setPosts(p); })
      .catch(() => { if (!cancelled) setPosts([]); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title="Blog"
        description="Product launches, deep-dives, and operator essays from the Worqly team."
      />

      <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={STAGGER}>
            <motion.div variants={FADE_UP}>
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                  bg-white/80 text-ink shadow-pill ring-1 ring-ink/5
                  dark:bg-white/10 dark:text-white dark:ring-white/10 dark:shadow-none"
              >
                <Sparkles className="h-3 w-3 text-coral" />
                Worqly journal
              </span>
            </motion.div>
            <motion.h1
              variants={FADE_UP}
              className="mt-6 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.02] text-balance"
            >
              From the <span className="italic font-serif text-coral">team building Worqly.</span>
            </motion.h1>
            <motion.p
              variants={FADE_UP}
              className="mt-6 text-body-lg text-ink/65 dark:text-white/65 max-w-2xl mx-auto text-pretty"
            >
              Launches, operator essays, and the occasional honest look at what we're getting wrong.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          {posts === null ? (
            <Skeleton />
          ) : posts.length === 0 ? (
            <EmptyState />
          ) : (
            <motion.ul
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              variants={STAGGER}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5"
            >
              {posts.map((p) => (
                <motion.li key={p.id} variants={FADE_UP}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="group block h-full rounded-3xl p-6 transition-all bg-white/85 ring-1 ring-ink/5 shadow-card hover:shadow-card-lg dark:bg-white/[0.04] dark:ring-white/10"
                  >
                    {p.cover_url && (
                      <img
                        src={p.cover_url}
                        alt=""
                        loading="lazy"
                        className="mb-4 w-full aspect-[16/9] object-cover rounded-2xl"
                      />
                    )}
                    <div className="flex items-center gap-2 text-[11.5px] text-ink/55 dark:text-white/55 tabular-nums">
                      <Calendar className="h-3 w-3" />
                      {p.published_at ? new Date(p.published_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                      {p.tags.slice(0, 2).map((t) => (
                        <span key={t} className="text-ink/45 dark:text-white/40">· {t}</span>
                      ))}
                    </div>
                    <h2 className="mt-3 text-[20px] font-semibold text-ink dark:text-white tracking-[-0.005em] leading-tight">
                      {p.title}
                    </h2>
                    {p.excerpt && (
                      <p className="mt-2 text-[14px] text-ink/65 dark:text-white/65 leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                    )}
                    {p.author_name && (
                      <p className="mt-4 text-[12.5px] font-semibold text-ink/65 dark:text-white/55">
                        — {p.author_name}
                      </p>
                    )}
                    <div className="mt-5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-coral group-hover:gap-2 transition-all">
                      Read more
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </Link>
                </motion.li>
              ))}
            </motion.ul>
          )}
        </div>
      </section>

      <FooterCTA />
    </div>
  );
}

function Skeleton() {
  return (
    <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {Array.from({ length: 4 }).map((_, i) => (
        <li
          key={i}
          className="rounded-3xl p-6 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card space-y-3"
        >
          <div className="aspect-[16/9] rounded-2xl bg-ink/10 dark:bg-white/10 animate-pulse" />
          <div className="h-3 w-24 rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
          <div className="h-5 w-3/4 rounded-2xl bg-ink/10 dark:bg-white/10 animate-pulse" />
          <div className="h-3 w-full rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl p-10 lg:p-14 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card text-center max-w-xl mx-auto">
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 mb-5">
        <FileText className="h-5 w-5" />
      </span>
      <Eyebrow className="!text-coral">Soon</Eyebrow>
      <h2 className="mt-2 text-[22px] font-semibold text-ink dark:text-white tracking-[-0.005em]">
        First post is being written.
      </h2>
      <p className="mt-3 text-[14px] text-ink/65 dark:text-white/65 leading-relaxed">
        We're warming up the keyboard. Check{' '}
        <Link to="/changelog" className="text-coral font-semibold hover:underline">changelog</Link>{' '}
        for the latest launches in the meantime.
      </p>
    </div>
  );
}

function FooterCTA() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">Start free</p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Try the product we're <em className="font-serif">writing about.</em>
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Seven days free, no card to start. Pick the modules you actually need.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14.5px] font-semibold hover:bg-black transition-colors"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/15 backdrop-blur-md text-white text-[14.5px] font-semibold ring-1 ring-white/25 hover:bg-white/25 transition-colors"
              >
                See pricing
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
